/**
 * Cfooter Component
 * Full-screen footer section with centered call-to-action button
 */
'use client';
import { useRef } from 'react';
import type { CSSProperties, ReactElement, RefObject } from 'react';
import { ColorType } from '@/theme';
import { useViewport } from '@/hooks/dom';
import { useViewportMetrics } from '@/hooks/dom';
import { Viewport } from '@/theme/breakpoints';
import { CfooterActions } from './CfooterActions';
import { CfooterBackground } from './CfooterBackground';
import { CfooterContactForm } from './CfooterContactForm';
import { useFooterActions } from './useFooterActions';
import { useFooterCoding } from './useFooterCoding';
import { useFooterContactForm } from './useFooterContactForm';
import { useFooterVisibility } from './useFooterVisibility';
export interface CfooterProps {
    className?: string;
    style?: CSSProperties;
    onContactClick?: () => void;
    onRevealEmailClick?: () => void;
    onAboutUsClick?: () => void;
    onPrivacyClick?: () => void;
    onTermsClick?: () => void;
    privacyHref?: string;
    termsHref?: string;
}
export interface CfooterContentProps {
    sectionRef: RefObject<HTMLElement | null>;
    onContactClick?: () => void;
    onRevealEmailClick?: () => void;
    onAboutUsClick?: () => void;
    onPrivacyClick?: () => void;
    onTermsClick?: () => void;
    privacyHref?: string;
    termsHref?: string;
}
export function Cfooter({ className, style, onContactClick, onRevealEmailClick, onAboutUsClick, onPrivacyClick, onTermsClick, privacyHref = '/privacy-policy', termsHref = '/terms-of-service', }: CfooterProps): ReactElement {
    const { vh } = useViewportMetrics();
    const sectionRef = useRef<HTMLElement>(null);
    const sectionStyle: CSSProperties = {
        position: 'relative',
        backgroundColor: ColorType.Black,
        height: vh * 100,
        minHeight: vh * 100,
        maxHeight: vh * 100,
        overflow: 'hidden',
        width: '100%',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        ...style,
    };
    return (
      <section data-el="Cfooter/Root" ref={sectionRef} className={className} style={sectionStyle}>
        <CfooterContent sectionRef={sectionRef} onContactClick={onContactClick} onRevealEmailClick={onRevealEmailClick} onAboutUsClick={onAboutUsClick} onPrivacyClick={onPrivacyClick} onTermsClick={onTermsClick} privacyHref={privacyHref} termsHref={termsHref}/>
      </section>
    );
}
export function CfooterContent({ sectionRef, onContactClick, onRevealEmailClick, onAboutUsClick, onPrivacyClick, onTermsClick, privacyHref = '/privacy-policy', termsHref = '/terms-of-service', }: CfooterContentProps): ReactElement {
    const { viewport } = useViewport();
    const { vh } = useViewportMetrics();
    const isMobile = viewport === Viewport.Mobile;
    const isVisible = useFooterVisibility({ sectionRef });
    const footerActions = useFooterActions({
        onRevealEmailClick,
        onAboutUsClick,
        onPrivacyClick,
        onTermsClick,
    });
    const { committedEmail, committedMessage, ...contactForm } = useFooterContactForm({ onContactClick });
    const footerCoding = useFooterCoding({
        contactStep: contactForm.contactStep,
        isMobile,
        committedEmail,
        committedMessage,
    });
    return (
      <>
        <CfooterBackground isVisible={isVisible} isMobile={isMobile} isSubmitting={contactForm.isSubmitting} contactStep={contactForm.contactStep} {...footerCoding}/>
        <CfooterActions vh={vh} footerState={footerActions.footerState} headerModel={footerActions.headerModel} privacyHref={privacyHref} termsHref={termsHref} onRevealEmail={footerActions.handleRevealEmail} onAboutUs={footerActions.handleAboutUs} onPrivacy={footerActions.handlePrivacy} onTerms={footerActions.handleTerms}/>
        <CfooterContactForm {...contactForm}/>
      </>
    );
}
